import { useMemo, useState } from "react";

import dayjs from "dayjs";
import { AiOutlineClose } from "react-icons/ai";

import { rejectConflictReservations } from "@/apis/reservation";
import ModalLayout from "@/components/Layout/ModalLayout";
import ConfirmModal from "@/components/Modal/ConfirmModal";
import { formatTime } from "@/pages/Reservation/components/ReservationTab/BatchReservationTab/constants";
import useToastStore from "@/store/toastStore";
import type {
  BatchPreviewResult,
  ConflictReservation,
} from "@/types/batchReservation";

interface ConflictManageModalProps {
  previewResults: BatchPreviewResult[];
  onClose: () => void;
  /** 반려가 한 건이라도 성공하면 호출된다. 미리보기를 재조회하는 쪽에서 처리한다. */
  onRejected: () => void;
}

/** 날짜별로 묶인 충돌 예약. 화면 표시 순서를 날짜순으로 고정한다. */
const groupByDate = (previewResults: BatchPreviewResult[]) =>
  previewResults
    .filter(result => result.conflicts.length > 0)
    .map(result => ({ date: result.date, conflicts: result.conflicts }));

const displayName = (conflict: ConflictReservation) =>
  conflict.userName || "알 수 없음";

/**
 * 일부만 선택된 그룹을 중간 상태로 표시하는 체크박스.
 * indeterminate 는 HTML 속성이 아니라 DOM 프로퍼티여서 ref 로만 설정할 수 있다.
 */
const TriStateCheckbox = ({
  checked,
  indeterminate,
  onChange,
  className,
}: {
  checked: boolean;
  indeterminate: boolean;
  onChange: () => void;
  className?: string;
}) => (
  <input
    type="checkbox"
    checked={checked}
    ref={el => {
      if (el) el.indeterminate = indeterminate;
    }}
    onChange={onChange}
    className={className}
  />
);

const displayDetail = (conflict: ConflictReservation) => {
  const time =
    conflict.startTime && conflict.endTime
      ? `${formatTime(conflict.startTime)}~${formatTime(conflict.endTime)}`
      : "";

  return [conflict.cmtNm, time, conflict.useCnts].filter(Boolean).join(" · ");
};

const ConflictManageModal = ({
  previewResults,
  onClose,
  onRejected,
}: ConflictManageModalProps) => {
  const groups = useMemo(() => groupByDate(previewResults), [previewResults]);
  const allIds = useMemo(
    () => groups.flatMap(group => group.conflicts.map(c => c.rsvtId)),
    [groups],
  );

  // 반려하려고 여는 화면이므로 전체 선택 상태로 시작한다.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(allIds),
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const showToast = useToastStore(state => state.showToast);

  const isAllSelected =
    allIds.length > 0 && allIds.every(id => selectedIds.has(id));

  const toggleOne = (rsvtId: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(rsvtId)) {
        next.delete(rsvtId);
      } else {
        next.add(rsvtId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(isAllSelected ? new Set() : new Set(allIds));
  };

  /** 날짜 하나의 충돌 예약을 통째로 켜고 끈다. 일부만 선택된 상태면 전체 선택으로 맞춘다. */
  const toggleDate = (dateIds: number[]) => {
    const isDateAllSelected = dateIds.every(id => selectedIds.has(id));

    setSelectedIds(prev => {
      const next = new Set(prev);
      dateIds.forEach(id => {
        if (isDateAllSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });
      return next;
    });
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    try {
      const { rejected, skipped } = await rejectConflictReservations([
        ...selectedIds,
      ]);

      if (rejected.length === 0) {
        showToast("이미 취소·반려된 예약입니다.", "info");
      } else if (skipped.length > 0) {
        showToast(
          `${rejected.length}건을 반려했습니다. (${skipped.length}건은 이미 취소·반려된 예약입니다)`,
          "success",
        );
      } else {
        showToast(`${rejected.length}건을 반려했습니다.`, "success");
      }

      setIsConfirmOpen(false);
      onClose();
      onRejected();
    } catch {
      // 실패 시 모달을 닫지 않는다. 선택 상태를 유지해 바로 재시도할 수 있게 한다.
      showToast("예약 반려 중 오류가 발생했습니다.", "error");
      setIsConfirmOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalLayout variant="primary" onClose={onClose}>
        <div className="flex max-h-[80vh] w-[92vw] flex-col sm:w-[560px]">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
            <h3 className="text-base font-bold text-gray-900">
              기간 내 충돌 예약 {allIds.length}건
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            >
              <AiOutlineClose size={16} />
            </button>
          </div>

          {/* 전체 선택 */}
          <div className="border-b border-gray-100 px-6 py-3">
            <label className="flex cursor-pointer items-center gap-2 text-small text-gray-dark">
              <TriStateCheckbox
                checked={isAllSelected}
                indeterminate={selectedIds.size > 0 && !isAllSelected}
                onChange={toggleAll}
                className="accent-primary"
              />
              전체 선택 ({selectedIds.size}/{allIds.length})
            </label>
          </div>

          {/* 날짜별 충돌 목록 */}
          <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
            {groups.map(group => {
              const dateIds = group.conflicts.map(c => c.rsvtId);
              const selectedInDate = dateIds.filter(id =>
                selectedIds.has(id),
              ).length;

              return (
                <div key={group.date} className="mb-4 last:mb-0">
                  <label className="mb-2 flex cursor-pointer items-center gap-2">
                    <TriStateCheckbox
                      checked={selectedInDate === dateIds.length}
                      indeterminate={
                        selectedInDate > 0 && selectedInDate < dateIds.length
                      }
                      onChange={() => toggleDate(dateIds)}
                      className="accent-primary"
                    />
                    <span className="text-small font-medium text-primary">
                      {dayjs(group.date, "YYYYMMDD").format("YYYY.MM.DD (ddd)")}
                    </span>
                    <span className="text-xs text-gray-400">
                      {selectedInDate}/{dateIds.length}
                    </span>
                  </label>
                  <div className="flex flex-col gap-1.5 pl-6">
                    {group.conflicts.map(conflict => (
                      <label
                        key={conflict.rsvtId}
                        className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(conflict.rsvtId)}
                          onChange={() => toggleOne(conflict.rsvtId)}
                          className="mt-1 accent-primary"
                        />
                        <span className="flex flex-col">
                          <span className="text-small text-gray-900">
                            {displayName(conflict)}
                          </span>
                          <span className="text-xs text-gray-500">
                            {displayDetail(conflict) || "상세 정보 없음"}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
            >
              닫기
            </button>
            <button
              type="button"
              onClick={() => setIsConfirmOpen(true)}
              disabled={selectedIds.size === 0 || isSubmitting}
              className="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:bg-gray-light disabled:text-gray-dull"
            >
              선택 {selectedIds.size}건 반려
            </button>
          </div>
        </div>
      </ModalLayout>

      {isConfirmOpen && (
        <ConfirmModal
          title="예약 반려"
          onConfirm={handleReject}
          onClose={() => setIsConfirmOpen(false)}
        >
          선택한 {selectedIds.size}건의 예약을 반려합니다. 반려된 예약은 되돌릴
          수 없습니다.
        </ConfirmModal>
      )}
    </>
  );
};

export default ConflictManageModal;
